// lib/layer-cut-utils.ts
import type { CropPoint, Rect } from '#types/types';
import { blurAlphaChannel } from '#lib/canny';
import {
  buildStudioLayerFrameEdgeGuardMask,
  type StudioLayerTransform,
} from '#lib/studio-layer-transform';

interface PolygonBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface CroppedImageDataResult {
  imageData: ImageData;
  offset: {
    minX: number;
    minY: number;
  };
}

export const NATIVE_LAYER_CUT_DEFAULT_BLEND_RADIUS = 16;
export const NATIVE_LAYER_CUT_MIN_BLEND_RADIUS = 0;
export const NATIVE_LAYER_CUT_MAX_BLEND_RADIUS = 64;
export const NATIVE_LAYER_CUT_PREVIEW_MAX_PIXELS = 2_000_000;

export interface NativeLayerCutImageBounds {
  minX: number;
  minY: number;
  width: number;
  height: number;
}

export const getNativeLayerCutImageBounds = (
  sourceWidth: number,
  sourceHeight: number,
  polygons: CropPoint[][],
  invert = false,
): NativeLayerCutImageBounds | null => {
  const normalizedPolygons = polygons
    .map(normalizePolygonClockwise)
    .filter((polygon) => polygon.length >= 3);
  const bounds = getPolygonBounds(normalizedPolygons);
  if (!bounds) return null;

  const minX = invert ? 0 : Math.max(0, Math.floor(bounds.minX));
  const minY = invert ? 0 : Math.max(0, Math.floor(bounds.minY));
  const maxX = invert ? sourceWidth : Math.min(sourceWidth, Math.ceil(bounds.maxX));
  const maxY = invert ? sourceHeight : Math.min(sourceHeight, Math.ceil(bounds.maxY));
  const width = maxX - minX;
  const height = maxY - minY;
  return width > 0 && height > 0 ? { minX, minY, width, height } : null;
};

export const getNativeLayerCutPreviewDimensions = (
  width: number,
  height: number,
  maxPixels: number = NATIVE_LAYER_CUT_PREVIEW_MAX_PIXELS,
): { width: number; height: number } => {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0 || maxPixels <= 0) {
    return { width: 0, height: 0 };
  }
  const safeWidth = Math.max(1, Math.floor(width));
  const safeHeight = Math.max(1, Math.floor(height));
  if (safeWidth * safeHeight <= maxPixels) {
    return { width: safeWidth, height: safeHeight };
  }
  const ratio = Math.sqrt(maxPixels / (safeWidth * safeHeight));
  return {
    width: Math.max(1, Math.floor(safeWidth * ratio)),
    height: Math.max(1, Math.floor(safeHeight * ratio)),
  };
};

export const getNativeLayerCutPreviewRadius = (
  blendRadius: number,
  sourceWidth: number,
  previewWidth: number,
): number => {
  if (!Number.isFinite(blendRadius) || blendRadius <= 0) return 0;
  if (!Number.isFinite(sourceWidth) || sourceWidth <= 0 || !Number.isFinite(previewWidth) || previewWidth <= 0) {
    return 1;
  }
  return Math.max(1, Math.round(blendRadius * previewWidth / sourceWidth));
};

export const clampNativeLayerCutBlendRadius = (value: unknown): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return NATIVE_LAYER_CUT_DEFAULT_BLEND_RADIUS;
  }
  return Math.min(
    NATIVE_LAYER_CUT_MAX_BLEND_RADIUS,
    Math.max(NATIVE_LAYER_CUT_MIN_BLEND_RADIUS, Math.round(value)),
  );
};

const isFinitePoint = (point: CropPoint): boolean =>
  Number.isFinite(point.x) && Number.isFinite(point.y);

const isClockwise = (polygon: CropPoint[]): boolean => {
  let sum = 0;
  for (let index = 0; index < polygon.length; index += 1) {
    const current = polygon[index];
    const next = polygon[(index + 1) % polygon.length];
    if (!current || !next) continue;
    sum += (next.x - current.x) * (next.y + current.y);
  }
  return sum > 0;
};

export const normalizePolygonClockwise = (polygon: CropPoint[]): CropPoint[] => {
  if (polygon.length < 3) return [];
  const safePolygon = polygon.filter(isFinitePoint);
  if (safePolygon.length < 3) return [];
  return isClockwise(safePolygon) ? safePolygon : [...safePolygon].reverse();
};

export const getPolygonBounds = (polygons: CropPoint[][]): PolygonBounds | null => {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  let hasPoint = false;

  for (const polygon of polygons) {
    for (const point of polygon) {
      if (!isFinitePoint(point)) continue;
      hasPoint = true;
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
    }
  }

  if (!hasPoint) return null;
  return { minX, minY, maxX, maxY };
};

export const buildNativeLayerCutPreviewFrameGuardMask = ({
  width,
  height,
  cutWidth,
  cutHeight,
  cutOffsetX,
  cutOffsetY,
  postCutLayerRect,
  frameRect,
  transform,
  layerScale,
  guardBandPx,
  transparentMargins,
  targetMask,
}: {
  width: number;
  height: number;
  cutWidth: number;
  cutHeight: number;
  cutOffsetX: number;
  cutOffsetY: number;
  postCutLayerRect: Rect;
  frameRect: Rect;
  transform: StudioLayerTransform;
  layerScale: number;
  guardBandPx: number;
  transparentMargins: { left: number; top: number; right: number; bottom: number };
  targetMask?: Uint8Array;
}): Uint8Array => {
  const safeWidth = Math.max(0, Math.floor(width));
  const safeHeight = Math.max(0, Math.floor(height));
  const safeCutWidth = Math.max(0, Math.floor(cutWidth));
  const safeCutHeight = Math.max(0, Math.floor(cutHeight));
  const guardMask = targetMask && targetMask.length === safeWidth * safeHeight
    ? targetMask.fill(0)
    : new Uint8Array(safeWidth * safeHeight);
  if (safeWidth === 0 || safeHeight === 0 || safeCutWidth === 0 || safeCutHeight === 0) return guardMask;

  const hasLayerTransform = Math.abs(transform.rotationDeg) > 0.0005 || transform.flipX || transform.flipY;
  if (hasLayerTransform) {
    const cutGuardMask = buildStudioLayerFrameEdgeGuardMask({
      width: safeCutWidth,
      height: safeCutHeight,
      layerRect: postCutLayerRect,
      transform,
      frameRect,
      guardBandPx: Math.max(0, Math.ceil(guardBandPx)),
      transparentMargins,
    });
    const offsetX = Math.floor(cutOffsetX);
    const offsetY = Math.floor(cutOffsetY);
    for (let y = 0; y < safeCutHeight; y += 1) {
      const targetY = y + offsetY;
      if (targetY < 0 || targetY >= safeHeight) continue;
      for (let x = 0; x < safeCutWidth; x += 1) {
        const targetX = x + offsetX;
        const guardWeight = cutGuardMask[y * safeCutWidth + x] ?? 0;
        if (targetX >= 0 && targetX < safeWidth && guardWeight > 0) {
          guardMask[targetY * safeWidth + targetX] = guardWeight;
        }
      }
    }
    return guardMask;
  }

  {
    const edgeEpsilon = Math.max(1, layerScale * 0.5);
    const frameLeft = frameRect.x;
    const frameTop = frameRect.y;
    const frameRight = frameRect.x + frameRect.w;
    const frameBottom = frameRect.y + frameRect.h;
    const layerRight = postCutLayerRect.x + postCutLayerRect.w;
    const layerBottom = postCutLayerRect.y + postCutLayerRect.h;
    const touchesFrameLeft = postCutLayerRect.x <= frameLeft + edgeEpsilon && layerRight >= frameLeft - edgeEpsilon;
    const touchesFrameTop = postCutLayerRect.y <= frameTop + edgeEpsilon && layerBottom >= frameTop - edgeEpsilon;
    const touchesFrameRight = postCutLayerRect.x <= frameRight + edgeEpsilon && layerRight >= frameRight - edgeEpsilon;
    const touchesFrameBottom = postCutLayerRect.y <= frameBottom + edgeEpsilon && layerBottom >= frameBottom - edgeEpsilon;
    const safeGuardBand = Math.max(0, Math.ceil(guardBandPx));
    const leftGuard = touchesFrameLeft
      ? Math.min(safeCutWidth, Math.max(0, Math.ceil(transparentMargins.left)) + safeGuardBand)
      : 0;
    const topGuard = touchesFrameTop
      ? Math.min(safeCutHeight, Math.max(0, Math.ceil(transparentMargins.top)) + safeGuardBand)
      : 0;
    const rightGuard = touchesFrameRight
      ? Math.min(safeCutWidth, Math.max(0, Math.ceil(transparentMargins.right)) + safeGuardBand)
      : 0;
    const bottomGuard = touchesFrameBottom
      ? Math.min(safeCutHeight, Math.max(0, Math.ceil(transparentMargins.bottom)) + safeGuardBand)
      : 0;
    const offsetX = Math.floor(cutOffsetX);
    const offsetY = Math.floor(cutOffsetY);
    for (let y = 0; y < safeCutHeight; y += 1) {
      const targetY = y + offsetY;
      if (targetY < 0 || targetY >= safeHeight) continue;
      for (let x = 0; x < safeCutWidth; x += 1) {
        const targetX = x + offsetX;
        if (targetX < 0 || targetX >= safeWidth) continue;
        let guardWeight = 0;
        if (leftGuard > 0 && x < leftGuard) {
          guardWeight = Math.max(guardWeight, Math.round((leftGuard - x) * 255 / leftGuard));
        }
        if (topGuard > 0 && y < topGuard) {
          guardWeight = Math.max(guardWeight, Math.round((topGuard - y) * 255 / topGuard));
        }
        if (rightGuard > 0 && x >= safeCutWidth - rightGuard) {
          guardWeight = Math.max(guardWeight, Math.round((x - (safeCutWidth - rightGuard) + 1) * 255 / rightGuard));
        }
        if (bottomGuard > 0 && y >= safeCutHeight - bottomGuard) {
          guardWeight = Math.max(guardWeight, Math.round((y - (safeCutHeight - bottomGuard) + 1) * 255 / bottomGuard));
        }
        guardMask[targetY * safeWidth + targetX] = Math.min(255, guardWeight);
      }
    }
  }
  return guardMask;
};

export const createCenterSquarePolygon = (width: number, height: number): CropPoint[] => {
  const safeWidth = Math.max(1, width);
  const safeHeight = Math.max(1, height);
  const side = Math.min(safeWidth, safeHeight);
  const offsetX = (safeWidth - side) / 2;
  const offsetY = (safeHeight - side) / 2;

  return [
    { x: offsetX, y: offsetY },
    { x: offsetX + side, y: offsetY },
    { x: offsetX + side, y: offsetY + side },
    { x: offsetX, y: offsetY + side },
  ];
};

const isPointOnSegment = (point: CropPoint, start: CropPoint, end: CropPoint): boolean => {
  const cross = (point.y - start.y) * (end.x - start.x) - (point.x - start.x) * (end.y - start.y);
  if (Math.abs(cross) > 0.000001) return false;
  return point.x >= Math.min(start.x, end.x) - 0.000001
    && point.x <= Math.max(start.x, end.x) + 0.000001
    && point.y >= Math.min(start.y, end.y) - 0.000001
    && point.y <= Math.max(start.y, end.y) + 0.000001;
};

const isPointInsidePolygon = (point: CropPoint, polygon: CropPoint[]): boolean => {
  let inside = false;
  for (let index = 0, previousIndex = polygon.length - 1; index < polygon.length; previousIndex = index++) {
    const current = polygon[index];
    const previous = polygon[previousIndex];
    if (!current || !previous) continue;
    if (isPointOnSegment(point, previous, current)) return true;

    const crossesScanline = (current.y > point.y) !== (previous.y > point.y);
    if (crossesScanline) {
      const intersectionX = ((previous.x - current.x) * (point.y - current.y))
        / (previous.y - current.y) + current.x;
      if (point.x < intersectionX) inside = !inside;
    }
  }
  return inside;
};

const isPointInsidePolygonUnion = (point: CropPoint, polygons: CropPoint[][]): boolean =>
  polygons.some((polygon) => isPointInsidePolygon(point, polygon));

/** Rasterises the polygon union, either keeping it or removing it from the source. */
export const cutImageDataByPolygons = (
  sourceImageData: ImageData,
  polygons: CropPoint[][],
  invert = false,
): CroppedImageDataResult | null => {
  const normalizedPolygons = polygons
    .map(normalizePolygonClockwise)
    .filter((polygon) => polygon.length >= 3);
  if (normalizedPolygons.length === 0) return null;

  const bounds = getPolygonBounds(normalizedPolygons);
  if (!bounds) return null;

  const minX = invert ? 0 : Math.max(0, Math.floor(bounds.minX));
  const minY = invert ? 0 : Math.max(0, Math.floor(bounds.minY));
  const maxX = invert ? sourceImageData.width : Math.min(sourceImageData.width, Math.ceil(bounds.maxX));
  const maxY = invert ? sourceImageData.height : Math.min(sourceImageData.height, Math.ceil(bounds.maxY));
  const width = maxX - minX;
  const height = maxY - minY;
  if (width <= 0 || height <= 0) return null;

  const imageData = new ImageData(width, height);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const sourceX = minX + x;
      const sourceY = minY + y;
      const isInside = isPointInsidePolygonUnion(
        { x: sourceX + 0.5, y: sourceY + 0.5 },
        normalizedPolygons,
      );
      const shouldKeep = invert ? !isInside : isInside;
      if (!shouldKeep) continue;

      const sourceIndex = (sourceY * sourceImageData.width + sourceX) * 4;
      const outputIndex = (y * width + x) * 4;
      imageData.data[outputIndex] = sourceImageData.data[sourceIndex] ?? 0;
      imageData.data[outputIndex + 1] = sourceImageData.data[sourceIndex + 1] ?? 0;
      imageData.data[outputIndex + 2] = sourceImageData.data[sourceIndex + 2] ?? 0;
      imageData.data[outputIndex + 3] = sourceImageData.data[sourceIndex + 3] ?? 0;
    }
  }

  return { imageData, offset: { minX, minY } };
};

export const renderKeepAreaPreview = (
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  width: number,
  height: number,
  polygons: CropPoint[][],
  dimOpacity: number = 0.7,
  invert = false,
): void => {
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(source, 0, 0, width, height);

  if (polygons.length === 0) return;

  ctx.fillStyle = `rgba(0, 0, 0, ${Math.max(0, Math.min(dimOpacity, 1))})`;
  if (invert) {
    ctx.save();
    ctx.beginPath();
    for (const polygon of polygons) {
      const normalized = normalizePolygonClockwise(polygon);
      const first = normalized[0];
      if (!first) continue;
      ctx.moveTo(first.x, first.y);
      for (let index = 1; index < normalized.length; index += 1) {
        const point = normalized[index];
        if (point) ctx.lineTo(point.x, point.y);
      }
      ctx.closePath();
    }
    ctx.fill();
    ctx.restore();
    return;
  }

  ctx.fillRect(0, 0, width, height);
  ctx.save();
  ctx.beginPath();
  for (const polygon of polygons) {
    const normalized = normalizePolygonClockwise(polygon);
    const first = normalized[0];
    if (!first) continue;
    ctx.moveTo(first.x, first.y);
    for (let index = 1; index < normalized.length; index += 1) {
      const point = normalized[index];
      if (point) ctx.lineTo(point.x, point.y);
    }
    ctx.closePath();
  }
  ctx.clip();
  ctx.drawImage(source, 0, 0, width, height);
  ctx.restore();
};

/** Sliding-window box blur along one axis; samples past the line edges count as 0. */
const boxBlurMaskPass = (
  source: Uint8ClampedArray,
  target: Uint8ClampedArray,
  radius: number,
  lineCount: number,
  lineLength: number,
  lineStartStride: number,
  sampleStride: number,
): void => {
  const windowSize = 2 * radius + 1;
  for (let line = 0; line < lineCount; line += 1) {
    const lineStart = line * lineStartStride;
    let sum = 0;
    for (let i = 0; i <= radius && i < lineLength; i += 1) {
      sum += source[lineStart + i * sampleStride] ?? 0;
    }
    for (let index = 0; index < lineLength; index += 1) {
      target[lineStart + index * sampleStride] = sum / windowSize;
      const add = index + 1 + radius;
      if (add < lineLength) sum += source[lineStart + add * sampleStride] ?? 0;
      const remove = index - radius;
      if (remove >= 0) sum -= source[lineStart + remove * sampleStride] ?? 0;
    }
  }
};

/**
 * Per-pixel dim alpha for the native-cut edge-softness preview. keptMask is 1 byte per pixel
 * (0 = removed, 255 = kept); the feather mirrors blurAlphaChannel's exact edge (same two-pass
 * box blur, same out-of-range = 0, same contrast restore) so the on-screen dim is the same
 * shape as the alpha the cut will really write.
 */
export interface CutBlendDimScratch {
  dim: Uint8ClampedArray;
  horizontal: Uint8ClampedArray;
  blurred: Uint8ClampedArray;
}

/** Keeps transparent cut pixels transparent while softly restoring the frame-edge alpha. */
export const blendAlphaWithFrameGuard = (
  sourceAlpha: number,
  blurredAlpha: number,
  frameGuardWeight: number = 0,
): number => {
  if (sourceAlpha <= 0) return 0;
  const weight = Math.max(0, Math.min(255, frameGuardWeight)) / 255;
  return Math.round(sourceAlpha * weight + blurredAlpha * (1 - weight));
};

export const computeCutBlendDimAlpha = (
  keptMask: Uint8ClampedArray,
  width: number,
  height: number,
  blendRadius: number,
  dimOpacity: number,
  frameGuardMask?: Uint8Array,
  scratch?: CutBlendDimScratch,
): Uint8ClampedArray => {
  const pixelCount = width * height;
  const dim = scratch?.dim.length === pixelCount ? scratch.dim : new Uint8ClampedArray(pixelCount);
  if (pixelCount <= 0) return dim;

  const fullDim = Math.round(Math.max(0, Math.min(dimOpacity, 1)) * 255);
  if (blendRadius <= 0) {
    for (let i = 0; i < pixelCount; i += 1) {
      dim[i] = (keptMask[i] ?? 0) > 0 ? 0 : fullDim;
    }
    return dim;
  }

  const radius = Math.max(1, Math.round(blendRadius));
  const horizontal = scratch?.horizontal.length === pixelCount
    ? scratch.horizontal
    : new Uint8ClampedArray(pixelCount);
  const blurred = scratch?.blurred.length === pixelCount
    ? scratch.blurred
    : new Uint8ClampedArray(pixelCount);
  boxBlurMaskPass(keptMask, horizontal, radius, height, width, width, 1);
  boxBlurMaskPass(horizontal, blurred, radius, width, height, 1, width);

  for (let i = 0; i < pixelCount; i += 1) {
    const isKept = (keptMask[i] ?? 0) > 0;
    const keptAlpha = isKept ? Math.max(2 * ((blurred[i] ?? 0) - 127) - 1, 1) : 0;
    const blendedAlpha = blendAlphaWithFrameGuard(
      isKept ? 255 : 0,
      keptAlpha,
      frameGuardMask?.[i] ?? 0,
    );
    dim[i] = isKept ? Math.round(fullDim * (1 - blendedAlpha / 255)) : fullDim;
  }
  return dim;
};

export const cropImageDataByPolygons = (
  sourceImageData: ImageData,
  polygons: CropPoint[][],
): CroppedImageDataResult | null => cutImageDataByPolygons(sourceImageData, polygons, false);

interface LayerRasterPlacement {
  rect: Rect;
  scale: number;
  transform: StudioLayerTransform;
  frameRect?: Rect;
}

interface LayerRasterJob {
  blendRadius: number;
  placement: LayerRasterPlacement;
  cut?: { polygons: CropPoint[][]; invert: boolean };
}

const resolveScalarFrameGuardWeight = (
  x: number,
  y: number,
  width: number,
  height: number,
  leftGuardPx: number,
  topGuardPx: number,
  rightGuardPx: number,
  bottomGuardPx: number,
): number => {
  let weight = 0;
  if (leftGuardPx > 0 && x < leftGuardPx) {
    weight = Math.max(weight, Math.round((leftGuardPx - x) * 255 / leftGuardPx));
  }
  if (topGuardPx > 0 && y < topGuardPx) {
    weight = Math.max(weight, Math.round((topGuardPx - y) * 255 / topGuardPx));
  }
  if (rightGuardPx > 0 && x >= width - rightGuardPx) {
    weight = Math.max(weight, Math.round((x - (width - rightGuardPx) + 1) * 255 / rightGuardPx));
  }
  if (bottomGuardPx > 0 && y >= height - bottomGuardPx) {
    weight = Math.max(weight, Math.round((y - (height - bottomGuardPx) + 1) * 255 / bottomGuardPx));
  }
  return Math.min(255, weight);
};

const computeBlendedImageData = (
  sourceImageData: ImageData,
  fadeRadius: number,
  leftGuardPx: number,
  topGuardPx: number,
  rightGuardPx: number,
  bottomGuardPx: number,
  frameGuardMask?: Uint8Array,
): ImageData => {
  const width = sourceImageData.width;
  const height = sourceImageData.height;

  const virtualMask = new ImageData(width, height);
  for (let i = 0; i < sourceImageData.data.length; i += 4) {
    const alpha = sourceImageData.data[i + 3];
    virtualMask.data[i] = 255;
    virtualMask.data[i + 1] = 255;
    virtualMask.data[i + 2] = 255;
    virtualMask.data[i + 3] = (alpha && alpha > 0) ? 255 : 0;
  }

  const blurredMask = fadeRadius > 0 ? blurAlphaChannel(virtualMask, fadeRadius) : virtualMask;
  const blendedImageData = new ImageData(width, height);

  for (let y = 0; y < height; y++) {
    const yw = y * width;
    for (let x = 0; x < width; x++) {
      const pixelIndex = (yw + x) * 4;
      const sourceAlpha = sourceImageData.data[pixelIndex + 3] ?? 0;
      const blurredAlpha = blurredMask.data[pixelIndex + 3] ?? 0;
      const rawFrameGuardWeight = frameGuardMask
        ? frameGuardMask[yw + x] ?? 0
        : resolveScalarFrameGuardWeight(
          x,
          y,
          width,
          height,
          leftGuardPx,
          topGuardPx,
          rightGuardPx,
          bottomGuardPx,
        );
      const frameGuardWeight = fadeRadius > 0
        ? rawFrameGuardWeight
        : rawFrameGuardWeight > 0 ? 255 : 0;

      blendedImageData.data[pixelIndex] = sourceImageData.data[pixelIndex] ?? 0;
      blendedImageData.data[pixelIndex + 1] = sourceImageData.data[pixelIndex + 1] ?? 0;
      blendedImageData.data[pixelIndex + 2] = sourceImageData.data[pixelIndex + 2] ?? 0;
      blendedImageData.data[pixelIndex + 3] = blendAlphaWithFrameGuard(sourceAlpha, blurredAlpha, frameGuardWeight);
    }
  }

  return blendedImageData;
};

const blendLayerImageData = (
  sourceImageData: ImageData,
  fadeRadius: number,
  placement: LayerRasterPlacement,
): ImageData => {
  const width = sourceImageData.width;
  const height = sourceImageData.height;
  const frame = placement.frameRect;
  const layerScale = placement.scale;
  const guardBandPx = Math.max(0, Math.ceil(fadeRadius));

  const layerTransform = placement.transform;
  const hasLayerTransform = Math.abs(layerTransform.rotationDeg) > 0.0005 || layerTransform.flipX || layerTransform.flipY;
  let frameGuardGeometry: { layerWorldRect: Rect; frameRect: Rect } | null = null;
  let touchesFrameLeft = false;
  let touchesFrameTop = false;
  let touchesFrameRight = false;
  let touchesFrameBottom = false;

  if (frame) {
    const layerWorldRect = {
      x: frame.x + placement.rect.x,
      y: frame.y + placement.rect.y,
      w: Number.isFinite(placement.rect.w) && placement.rect.w > 0
        ? placement.rect.w
        : width * layerScale,
      h: Number.isFinite(placement.rect.h) && placement.rect.h > 0
        ? placement.rect.h
        : height * layerScale,
    };
    if (hasLayerTransform) {
      frameGuardGeometry = { layerWorldRect, frameRect: frame };
    } else {
      const edgeEpsilon = Math.max(1, layerScale * 0.5);
      const frameLeft = frame.x;
      const frameTop = frame.y;
      const frameRight = frame.x + frame.w;
      const frameBottom = frame.y + frame.h;
      const layerRight = layerWorldRect.x + layerWorldRect.w;
      const layerBottom = layerWorldRect.y + layerWorldRect.h;

      // Preserve the existing scalar guards for untransformed layers.
      touchesFrameLeft = layerWorldRect.x <= frameLeft + edgeEpsilon && layerRight >= frameLeft - edgeEpsilon;
      touchesFrameTop = layerWorldRect.y <= frameTop + edgeEpsilon && layerBottom >= frameTop - edgeEpsilon;
      touchesFrameRight = layerWorldRect.x <= frameRight + edgeEpsilon && layerRight >= frameRight - edgeEpsilon;
      touchesFrameBottom = layerWorldRect.y <= frameBottom + edgeEpsilon && layerBottom >= frameBottom - edgeEpsilon;
    }
  }

  let minOpaqueX = width;
  let minOpaqueY = height;
  let maxOpaqueX = -1;
  let maxOpaqueY = -1;
  for (let y = 0; y < height; y++) {
    const yw = y * width;
    for (let x = 0; x < width; x++) {
      const alpha = sourceImageData.data[(yw + x) * 4 + 3] ?? 0;
      if (alpha > 0) {
        if (x < minOpaqueX) minOpaqueX = x;
        if (y < minOpaqueY) minOpaqueY = y;
        if (x > maxOpaqueX) maxOpaqueX = x;
        if (y > maxOpaqueY) maxOpaqueY = y;
      }
    }
  }

  const hasOpaquePixels = maxOpaqueX >= 0 && maxOpaqueY >= 0;
  const leftTransparentMargin = hasOpaquePixels ? minOpaqueX : width;
  const topTransparentMargin = hasOpaquePixels ? minOpaqueY : height;
  const rightTransparentMargin = hasOpaquePixels ? (width - 1 - maxOpaqueX) : width;
  const bottomTransparentMargin = hasOpaquePixels ? (height - 1 - maxOpaqueY) : height;

  const frameGuardMask = frameGuardGeometry
    ? buildStudioLayerFrameEdgeGuardMask({
      width,
      height,
      layerRect: frameGuardGeometry.layerWorldRect,
      transform: layerTransform,
      frameRect: frameGuardGeometry.frameRect,
      guardBandPx,
      transparentMargins: {
        left: leftTransparentMargin,
        top: topTransparentMargin,
        right: rightTransparentMargin,
        bottom: bottomTransparentMargin,
      },
    })
    : undefined;
  const leftGuardPx = frameGuardMask ? 0 : (touchesFrameLeft ? Math.min(width, leftTransparentMargin + guardBandPx) : 0);
  const topGuardPx = frameGuardMask ? 0 : (touchesFrameTop ? Math.min(height, topTransparentMargin + guardBandPx) : 0);
  const rightGuardPx = frameGuardMask ? 0 : (touchesFrameRight ? Math.min(width, rightTransparentMargin + guardBandPx) : 0);
  const bottomGuardPx = frameGuardMask ? 0 : (touchesFrameBottom ? Math.min(height, bottomTransparentMargin + guardBandPx) : 0);

  return computeBlendedImageData(
    sourceImageData,
    fadeRadius,
    leftGuardPx,
    topGuardPx,
    rightGuardPx,
    bottomGuardPx,
    frameGuardMask,
  );
};

export const computeLayerRaster = (sourceImageData: ImageData, job: LayerRasterJob): ImageData | null => {
  if (!job.cut) return blendLayerImageData(sourceImageData, job.blendRadius, job.placement);
  const cutResult = cutImageDataByPolygons(sourceImageData, job.cut.polygons, job.cut.invert);
  if (!cutResult) return null;
  return job.blendRadius > 0
    ? blendLayerImageData(cutResult.imageData, job.blendRadius, job.placement)
    : cutResult.imageData;
};
